<div class="foundation-demo">
  <header class="foundation-demo__header">
    <div class="foundation-demo__container">
      <h1 class="foundation-demo__title">Foundation Components Demo</h1>
      <p class="foundation-demo__subtitle">
        Phase 1 of the ACS Chapter Portal has been successfully initialized. 
        This page demonstrates all the foundational components and theme system.
      </p>
    </div>
  </header>

  <main class="foundation-demo__main">
    <div class="foundation-demo__container">
      
      <!-- Phase 1 Completion Summary -->
      <section class="foundation-demo__section">
        <h2>✅ Phase 1 Complete</h2>
        <div class="foundation-demo__feature-list">
          <div class="foundation-demo__feature-item">
            <span class="foundation-demo__check">✓</span>
            <span>Angular 20 with standalone components</span>
          </div>
          <div class="foundation-demo__feature-item">
            <span class="foundation-demo__check">✓</span>
            <span>Angular Elements for web components</span>
          </div>
          <div class="foundation-demo__feature-item">
            <span class="foundation-demo__check">✓</span>
            <span>MemberJunction integration configured</span>
          </div>
          <div class="foundation-demo__feature-item">
            <span class="foundation-demo__check">✓</span>
            <span>ACS brand colors and typography</span>
          </div>
          <div class="foundation-demo__feature-item">
            <span class="foundation-demo__check">✓</span>
            <span>Comprehensive theme system with runtime customization</span>
          </div>
          <div class="foundation-demo__feature-item">
            <span class="foundation-demo__check">✓</span>
            <span>Shared component library with data grid, navigation, and more</span>
          </div>
        </div>
      </section>

      <!-- Component Demos -->
      <section class="foundation-demo__section">
        <h2>Component Demonstrations</h2>
        
        <!-- Loading Spinner -->
        <div class="foundation-demo__component">
          <h3>Loading Spinner</h3>
          <div class="foundation-demo__component-content foundation-demo__component-content--inline">
            <acs-loading-spinner size="small"></acs-loading-spinner>
            <acs-loading-spinner size="medium" message="Loading..."></acs-loading-spinner>
            <acs-loading-spinner size="large"></acs-loading-spinner>
          </div>
        </div>

        <!-- Buttons -->
        <div class="foundation-demo__component">
          <h3>Button Variants</h3>
          <div class="foundation-demo__component-content">
            <div class="foundation-demo__button-row">
              <acs-button variant="primary" (clicked)="handleButtonClick()">Primary Button</acs-button>
              <acs-button variant="secondary">Secondary Button</acs-button>
              <acs-button variant="text">Text Button</acs-button>
              <acs-button variant="danger">Danger Button</acs-button>
            </div>
            
            <div class="foundation-demo__button-row">
              <acs-button variant="primary" size="small">Small</acs-button>
              <acs-button variant="primary" size="medium">Medium</acs-button>
              <acs-button variant="primary" size="large">Large</acs-button>
            </div>
            
            <div class="foundation-demo__button-row">
              <acs-button variant="primary" [disabled]="true">Disabled</acs-button>
              <acs-button variant="primary" [loading]="isLoading">Loading State</acs-button>
              <acs-button variant="primary" [fullWidth]="true">Full Width Button</acs-button>
            </div>
          </div>
        </div>

        <!-- Alert Components -->
        <div class="foundation-demo__component">
          <h3>Alert Components</h3>
          <div class="foundation-demo__component-content">
            <acs-alert type="info" title="Information" message="This is an informational message with a title."></acs-alert>
            <acs-alert type="success" message="Chapter data loaded successfully!"></acs-alert>
            <acs-alert type="warning" message="Some chapters have not reported recent activity." [dismissible]="true"></acs-alert>
            <acs-alert type="error" title="Error" message="Failed to load data. Please try again." [dismissible]="true"></acs-alert>
          </div>
        </div>

        <!-- Modal Demo -->
        <div class="foundation-demo__component">
          <h3>Modal Component</h3>
          <div class="foundation-demo__component-content">
            <acs-button variant="primary" (clicked)="openModal()">Open Modal Demo</acs-button>
          </div>
        </div>
      </section>

      <!-- Typography Section -->
      <section class="foundation-demo__section">
        <h2>Typography System</h2>
        <div class="foundation-demo__typography">
          <h1>Heading 1 - Display Font (DM Serif Text)</h1>
          <h2>Heading 2 - Display Font</h2>
          <h3>Heading 3 - Display Font</h3>
          <p>Body text using Open Sans font family. This is the primary font for all body content throughout the application.</p>
          <p class="text-secondary">Secondary text color for less prominent information and supporting content.</p>
          <p class="text-small">Small text for captions, metadata, and fine print.</p>
        </div>
      </section>

      <!-- Color Palette -->
      <section class="foundation-demo__section">
        <h2>ACS Brand Color Palette</h2>
        <div class="foundation-demo__color-grid">
          <div class="foundation-demo__color-swatch foundation-demo__color-swatch--primary">
            <div class="foundation-demo__swatch"></div>
            <div class="foundation-demo__swatch-label">Primary Blue<br>#0F2144</div>
          </div>
          <div class="foundation-demo__color-swatch foundation-demo__color-swatch--digital-red">
            <div class="foundation-demo__swatch"></div>
            <div class="foundation-demo__swatch-label">Digital Red<br>#E91F00</div>
          </div>
          <div class="foundation-demo__color-swatch foundation-demo__color-swatch--steel-blue">
            <div class="foundation-demo__swatch"></div>
            <div class="foundation-demo__swatch-label">Steel Blue<br>#8AA3B0</div>
          </div>
          <div class="foundation-demo__color-swatch foundation-demo__color-swatch--success">
            <div class="foundation-demo__swatch"></div>
            <div class="foundation-demo__swatch-label">Success<br>#00892F</div>
          </div>
          <div class="foundation-demo__color-swatch foundation-demo__color-swatch--warning">
            <div class="foundation-demo__swatch"></div>
            <div class="foundation-demo__swatch-label">Warning<br>#FFA800</div>
          </div>
        </div>
      </section>

      <!-- Data Grid Demo -->
      <section class="foundation-demo__section">
        <h2>Data Grid Component</h2>
        <p class="text-secondary">
          The data grid demonstrates sorting, filtering, selection, export, and responsive design features.
          Try selecting multiple rows to see bulk actions functionality.
        </p>
        <div class="foundation-demo__component">
          <div class="foundation-demo__component-content">
            <acs-data-grid
              [data]="gridData"
              [columns]="gridColumns"
              [sortable]="true"
              [filterable]="true"
              [searchable]="true"
              searchPlaceholder="Search chapters..."
              [selectable]="'multiple'"
              [exportable]="true"
              [virtualScrolling]="false"
              [stickyHeader]="true"
              emptyText="No chapters found"
              (rowClick)="onGridRowClick($event)"
              (selectionChange)="onGridSelectionChange($event)"
              (export)="onGridExport($event)">
            </acs-data-grid>
          </div>
        </div>
      </section>

      <!-- Theme Configuration -->
      <section class="foundation-demo__section">
        <h2>Administrative Theme Configuration</h2>
        <p class="text-secondary">
          The theme system enables runtime customization of colors, spacing, and visual properties.
          This administrative interface would normally be restricted to system administrators with proper role-based access controls.
        </p>
        
        <div class="admin-theme-config">
          <div class="admin-header">
            <h2>🎨 System Administration - Theme Configuration</h2>
            <p class="admin-description">
              Configure the visual appearance and accessibility settings for the ACS Chapter Portal. 
              Changes will apply system-wide and can be overridden at the chapter level.
            </p>
          </div>

          <div class="admin-content">
            <acs-theme-selector></acs-theme-selector>
          </div>

          <div class="admin-info">
            <div class="info-card">
              <h4>📋 Administrative Notes</h4>
              <ul>
                <li><strong>Brand Compliance:</strong> All theme changes maintain ACS brand guidelines and accessibility standards</li>
                <li><strong>Chapter Override:</strong> Individual chapters can customize colors within approved parameters</li>
                <li><strong>Accessibility:</strong> High contrast theme ensures WCAG AAA compliance for all users</li>
                <li><strong>Persistence:</strong> Theme settings are automatically saved and applied across user sessions</li>
              </ul>
            </div>

            <div class="info-card">
              <h4>🚀 Future Administrative Features</h4>
              <ul>
                <li>Theme presets management and sharing</li>
                <li>Global vs chapter-specific theme controls</li>
                <li>Bulk theme application across multiple chapters</li>
                <li>Theme usage analytics and reporting</li>
                <li>Automated brand compliance validation</li>
                <li>Role-based access controls for theme management</li>
              </ul>
            </div>
          </div>
        </div>

      </section>

    </div>
  </main>
</div>

<!-- Modal Demo -->
<acs-modal
  [isOpen]="showModal"
  title="Demo Modal"
  size="medium"
  (closed)="closeModal()">
  <div class="modal-demo-content">
    <p>This is a demonstration of the ACS Modal component with:</p>
    <ul>
      <li>Customizable sizes (small, medium, large, fullscreen)</li>
      <li>Backdrop click to close</li>
      <li>Escape key support</li>
      <li>Focus trap for accessibility</li>
      <li>Smooth animations</li>
    </ul>
    <div class="modal-actions">
      <acs-button variant="secondary" (clicked)="closeModal()">Cancel</acs-button>
      <acs-button variant="primary" (clicked)="closeModal()">Confirm</acs-button>
    </div>
  </div>
</acs-modal>